import { makeStyles } from '@material-ui/styles'
import ListTableField from 'core/components/validatedForm/ListTableField'
import ModalForm from 'core/elements/modal/ModalForm'
import Text from 'core/elements/Text'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import ServiceUnhealthyInfo from 'openstack/components/common/ServiceUnhealthyInfo'
import ApiClient from 'api-client/ApiClient'
import { useCallback, useEffect, useState } from 'react'
import { isVolumeBackedVm } from '../../vms/helpers'
import { VirtualMachineLite } from '../../vms/newModel'
import { attachVolume } from './actions'

const useStyles = makeStyles<Theme>((theme) => ({
  fields: {
    gap: 16,
    display: 'grid',
  },
}))

const vmColumns = [
  {
    id: 'name',
    label: 'Available VMs',
    width: 'medium',
  },
  {
    id: 'state',
    label: 'State',
  },
]

const { nova } = ApiClient.getInstance()

const DESIRED_VM_FIELDS = ['name', 'id', 'state', 'volumeDetails']

export default function AttachVolumeDialog({ rows: [volume], onClose, reloadVolumes }) {
  const classes = useStyles()
  const defaultParams = {
    vm: null,
  }
  const { params, getParamsUpdater, setParams } = useParams(defaultParams)
  const { update, updating, error, reset } = useUpdateAction(attachVolume)
  const [loadingVms, setLoadingVms] = useState(false)
  const [vmsList, setVmsList] = useState<VirtualMachineLite[]>([])

  useEffect(() => {
    const fetchAvailableVMsList = async () => {
      try {
        setLoadingVms(true)
        const response = await Promise.allSettled([
          nova.getAllInstancesLite(`vm_state=ACTIVE`, null, DESIRED_VM_FIELDS),
          nova.getAllInstancesLite(`vm_state=STOPPED`, null, DESIRED_VM_FIELDS),
          nova.getAllInstancesLite(`vm_state=SUSPENDED`, null, DESIRED_VM_FIELDS),
        ])

        const localVMList: VirtualMachineLite[] = []
        response.forEach((r) => {
          if (r.status === 'fulfilled') {
            localVMList.push(...(r.value || []))
          }
        })
        const volumeBackedVms = localVMList.filter((vm) => isVolumeBackedVm(vm))
        setVmsList(volumeBackedVms)
      } catch {
        setVmsList([])
      } finally {
        setLoadingVms(false)
      }
    }

    fetchAvailableVMsList()
  }, [])

  const handleSubmit = useCallback(async () => {
    reset()
    const vm = params.vm[0]

    const body = {
      volumeAttachment: {
        volumeId: volume.id,
      },
    }
    const { error } = await update({
      id: volume.id,
      vmId: vm.id,
      body,
    })
    if (!error) {
      reloadVolumes?.()
      handleClose(true)
    }
  }, [params, volume, update, reset, reloadVolumes])

  const handleClose = (success?: boolean) => {
    setParams(defaultParams)
    reset()
    onClose(success)
  }

  return (
    <ModalForm
      title="Attach Volume"
      onSubmit={handleSubmit}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Attach Volume`}
      panel="dialog"
      maxWidth={1200}
      open
    >
      <ServiceUnhealthyInfo action="Volume Attachment" hypervisor blockStorage />
      <div className={classes.fields}>
        <Text variant="body2">Select the VM that you would like to attach the volume to.</Text>
        <Text variant="body2">
          You can attach a volume to at most one VM. Attaching a volume to a VM makes it accessible
          to that VM as an independent disk.
        </Text>
        <Text variant="body2">
          <b>NOTE</b>: A VM must be in <b>active</b>, <b>suspended</b>, or <b>stopped</b> state
          before you can attach a volume to it. Only VMs in these states are listed below.
        </Text>
        <ListTableField
          id="vm"
          data={vmsList}
          loading={loadingVms}
          columns={vmColumns}
          onChange={getParamsUpdater('vm')}
          value={params.vm}
          uniqueIdentifier="id"
          searchTargets={['name', 'id']}
          required
        />
      </div>
    </ModalForm>
  )
}
